// Manual grant awards.
//
// Grants issued outside the app process (live pitch competitions, school
// events, partner programs) are logged from the admin Grant awards tab as
// docs in the `pitches` collection carrying `source: "manual"`. They ride the
// same rails as application winners — syncPublicWinner mirrors them into
// publicWinners once published, so public totals and the awardees surfaces
// need no special casing — but they are NOT applications. Review flows and
// the application-pipeline analytics must exclude them via isManualAward.
//
// A manual award's createdAt/awardedAt is back-dated into the quarter the
// grant was issued (see quarterToDate). Every quarter label in the app is
// derived from createdAt, so the back-dated timestamp is what makes the
// selected quarter show up consistently in the portal, the Statistics tab,
// and the public awardees grid. Deliberately NO stored `quarter` field: the
// Completionist badge query (functions/statsTracking.js) counts pitches by
// stored quarter, and a manual award must never enter a reviewer's
// completion denominator.

export const MANUAL_AWARD_SOURCE = "manual";

// GNF's first grants went out in 2023 — the earliest quarter an award can be
// logged against.
export const MANUAL_AWARD_FIRST_YEAR = 2023;

export function isManualAward(pitch) {
  return !!pitch && pitch.source === MANUAL_AWARD_SOURCE;
}

function quarterOf(date) {
  return Math.floor(date.getMonth() / 3) + 1;
}

// "Q1 2023" … current quarter, newest first, for the quarter-issued select.
export function manualAwardQuarterOptions(now = new Date()) {
  const options = [];
  const currentYear = now.getFullYear();
  const currentQuarter = quarterOf(now);
  for (let year = currentYear; year >= MANUAL_AWARD_FIRST_YEAR; year--) {
    const startQuarter = year === currentYear ? currentQuarter : 4;
    for (let q = startQuarter; q >= 1; q--) {
      options.push(`Q${q} ${year}`);
    }
  }
  return options;
}

// Timestamp source for a manual award: the current quarter uses "now" (an
// honest creation time), past quarters use noon on the 15th of the quarter's
// middle month — weeks away from any quarter boundary in every timezone, so
// derived quarter labels can't drift off by one.
export function quarterToDate(quarterString, now = new Date()) {
  const match = /^Q([1-4]) (\d{4})$/.exec(quarterString || "");
  if (!match) return null;
  const q = Number(match[1]);
  const year = Number(match[2]);
  if (year === now.getFullYear() && q === quarterOf(now)) return now;
  return new Date(year, (q - 1) * 3 + 1, 15, 12, 0, 0);
}
